import { useEffect, useMemo, useRef, useState } from "react";
import QuestionLayout from "./QuestionLayout";
import SymbolPalette from "./staff/SymbolPalette";
import { Glyph } from "./staff/glyphs";
import { TIME_SIG_PATHS } from "./staff/timesigPaths";
import { rhythmComposeWidth } from "../rhythmLineComposeSymbols.js";
import {
  isBeamableRhythm,
  normalizeRhythmBeamLinks,
  rhythmBeamDepth
} from "../rhythmLineBeaming.js";

const LINE_Y = 52;
const BAR_TOP = 18;
const BAR_BOTTOM = 82;
const COUNT_INPUT_Y = 91;
const START_X = 8;
const FIRST_CONTENT_OFFSET = 78;
const MEASURE_PADDING = 16;
const SYMBOL_GAP = 14;
const TIME_SIG_SCALE = 0.038;
const TIME_SIG_DIGIT_GAP = 15;
const STEM_X_OFFSET = 10.7;
const BEAM_Y = LINE_Y - 35.5;
const BEAM_THICKNESS = 4;
const BEAM_LEVEL_GAP = 6;
const BEAM_HOOK_LENGTH = 10;

function parseTimeSignature(timeSig) {
  const [rawTop = "4", rawBottom = "4"] = String(timeSig || "4/4").split("/");
  return {
    top: rawTop.replace(/\D/g, "") || "4",
    bottom: rawBottom.replace(/\D/g, "") || "4"
  };
}

function renderTimeSignatureNumber(number, centerX, baselineY) {
  const digits = String(number).split("");
  const totalWidth = Math.max(0, digits.length - 1) * TIME_SIG_DIGIT_GAP;

  return digits.map((digit, index) => {
    const path = TIME_SIG_PATHS[digit];
    if (!path) return null;
    const digitCenter = centerX - totalWidth / 2 + index * TIME_SIG_DIGIT_GAP;

    return (
      <g
        key={`${digit}-${index}`}
        transform={`translate(${digitCenter}, ${baselineY}) scale(${TIME_SIG_SCALE}, -${TIME_SIG_SCALE}) translate(-150, 0)`}
        fill="#000"
      >
        <path d={path} />
      </g>
    );
  });
}

function normalizeMeasures(value, count) {
  const existing = Array.isArray(value?.rhythm?.measures) ? value.rhythm.measures : [];
  return Array.from({ length: count }, (_, index) => {
    const rhythms = Array.isArray(existing[index]?.rhythms) ? existing[index].rhythms : [];
    const existingCounts = Array.isArray(existing[index]?.counts) ? existing[index].counts : [];
    return {
      rhythms,
      counts: rhythms.map((_, rhythmIndex) => existingCounts[rhythmIndex] ?? ""),
      beamLinks: normalizeRhythmBeamLinks(existing[index]?.beamLinks, rhythms.length)
    };
  });
}

function contentWidth(symbols = []) {
  if (!symbols.length) return 0;
  return symbols.reduce((sum, symbol) => sum + rhythmComposeWidth(symbol), 0) +
    Math.max(0, symbols.length - 1) * SYMBOL_GAP;
}

function linkColor(mode, part) {
  if (mode !== "review") return "#000";
  if (part?.correct === true) return "#2e7d32";
  if (part?.correct === false) return "#c62828";
  return "#000";
}

function BeamOverlay({
  items,
  links,
  measureIndex,
  mode,
  phase,
  feedback,
  onToggle
}) {
  const activeLinks = normalizeRhythmBeamLinks(links, items.length);
  const elements = [];

  activeLinks.forEach((active, gapIndex) => {
    if (!active) return;
    const left = items[gapIndex];
    const right = items[gapIndex + 1];
    if (!left || !right || !isBeamableRhythm(left.symbol) || !isBeamableRhythm(right.symbol)) return;

    const x1 = left.x + STEM_X_OFFSET;
    const x2 = right.x + STEM_X_OFFSET;
    const depth = Math.min(rhythmBeamDepth(left.symbol), rhythmBeamDepth(right.symbol));
    const part = feedback?.beamParts?.[`m${measureIndex}-g${gapIndex}`];
    const fill = linkColor(mode, part);

    for (let level = 1; level <= depth; level += 1) {
      elements.push(
        <rect
          key={`beam-${gapIndex}-${level}`}
          x={x1}
          y={BEAM_Y + (level - 1) * BEAM_LEVEL_GAP}
          width={Math.max(1, x2 - x1 + 1)}
          height={BEAM_THICKNESS}
          fill={fill}
          pointerEvents="none"
        />
      );
    }
  });

  // A 16th/32nd that is beamed to a shorter-value neighbor still needs a
  // partial secondary/tertiary beam. Those hooks are derived automatically.
  items.forEach((item, itemIndex) => {
    const depth = rhythmBeamDepth(item.symbol);
    if (depth < 2) return;

    const leftConnected = itemIndex > 0 && activeLinks[itemIndex - 1] === true;
    const rightConnected = itemIndex < activeLinks.length && activeLinks[itemIndex] === true;
    if (!leftConnected && !rightConnected) return;

    for (let level = 2; level <= depth; level += 1) {
      const leftDepth = itemIndex > 0 ? rhythmBeamDepth(items[itemIndex - 1]?.symbol) : 0;
      const rightDepth = itemIndex + 1 < items.length ? rhythmBeamDepth(items[itemIndex + 1]?.symbol) : 0;
      const sameLevelLeft = leftConnected && leftDepth >= level;
      const sameLevelRight = rightConnected && rightDepth >= level;
      if (sameLevelLeft || sameLevelRight) continue;

      const hookRight = rightConnected || !leftConnected;
      const connectionIndex = hookRight ? itemIndex : itemIndex - 1;
      const part = feedback?.beamParts?.[`m${measureIndex}-g${connectionIndex}`];
      const fill = linkColor(mode, part);
      const stemX = item.x + STEM_X_OFFSET;
      const x = hookRight ? stemX : stemX - BEAM_HOOK_LENGTH;

      elements.push(
        <rect
          key={`hook-${itemIndex}-${level}`}
          x={x}
          y={BEAM_Y + (level - 1) * BEAM_LEVEL_GAP}
          width={BEAM_HOOK_LENGTH}
          height={BEAM_THICKNESS}
          fill={fill}
          pointerEvents="none"
        />
      );
    }
  });

  // Review mode also shows where a required connection was omitted.
  if (mode === "review") {
    for (let gapIndex = 0; gapIndex < Math.max(0, items.length - 1); gapIndex += 1) {
      const part = feedback?.beamParts?.[`m${measureIndex}-g${gapIndex}`];
      if (!part || part.expected !== true || part.received === true) continue;
      const left = items[gapIndex];
      const right = items[gapIndex + 1];
      if (!left || !right) continue;

      elements.push(
        <line
          key={`missing-${gapIndex}`}
          x1={left.x + STEM_X_OFFSET}
          x2={right.x + STEM_X_OFFSET}
          y1={BEAM_Y + BEAM_THICKNESS / 2}
          y2={BEAM_Y + BEAM_THICKNESS / 2}
          stroke="#c62828"
          strokeWidth="3"
          strokeDasharray="5 4"
          pointerEvents="none"
        />
      );
    }
  }

  if (mode === "edit" && phase === "beam") {
    for (let gapIndex = 0; gapIndex < Math.max(0, items.length - 1); gapIndex += 1) {
      const left = items[gapIndex];
      const right = items[gapIndex + 1];
      if (!left || !right || !isBeamableRhythm(left.symbol) || !isBeamableRhythm(right.symbol)) continue;

      const x1 = left.x + STEM_X_OFFSET;
      const x2 = right.x + STEM_X_OFFSET;
      const active = activeLinks[gapIndex] === true;

      if (!active) {
        elements.push(
          <line
            key={`placeholder-${gapIndex}`}
            x1={x1}
            x2={x2}
            y1={BEAM_Y + BEAM_THICKNESS / 2}
            y2={BEAM_Y + BEAM_THICKNESS / 2}
            stroke="#8b949e"
            strokeWidth="3"
            strokeDasharray="5 4"
            pointerEvents="none"
          />
        );
      }

      elements.push(
        <rect
          key={`beam-hit-${gapIndex}`}
          x={x1 - 5}
          y={BEAM_Y - 10}
          width={Math.max(28, x2 - x1 + 10)}
          height={32}
          fill="transparent"
          style={{ cursor: "pointer" }}
          onClick={(event) => {
            event.stopPropagation();
            onToggle?.(gapIndex);
          }}
        >
          <title>{active ? "Remove beam connection" : "Add beam connection"}</title>
        </rect>
      );
    }
  }

  return <g>{elements}</g>;
}

export default function RhythmLineComposeBeaming({
  config = {},
  value = {},
  onChange,
  mode = "edit",
  feedback
}) {
  const disabled = mode !== "edit";
  const measureCount = Math.max(1, Number(config.measures) || 1);
  const includeCounts = config.includeCounts === true;
  const allowedSymbols = Array.isArray(config.allowedSymbols) && config.allowedSymbols.length
    ? config.allowedSymbols
    : Array.isArray(config.requiredSymbols)
      ? config.requiredSymbols
      : [];
  const requiredSymbols = Array.isArray(config.requiredSymbols) ? config.requiredSymbols : allowedSymbols;
  const baseMeasureWidth = Math.max(150, Number(config.measureWidth) || 260);
  const measures = normalizeMeasures(value, measureCount);
  const [currentMeasure, setCurrentMeasure] = useState(0);
  const [phase, setPhase] = useState("compose");
  const containerRef = useRef(null);
  const scrollRef = useRef(null);
  const { top, bottom } = parseTimeSignature(config.timeSig);

  useEffect(() => {
    if (currentMeasure >= measureCount) setCurrentMeasure(measureCount - 1);
  }, [currentMeasure, measureCount]);

  const measureWidths = useMemo(() => measures.map((measure, index) => {
    const leading = index === 0 ? FIRST_CONTENT_OFFSET : MEASURE_PADDING;
    return Math.max(
      baseMeasureWidth,
      leading + contentWidth(measure.rhythms) + MEASURE_PADDING
    );
  }), [measures, baseMeasureWidth]);

  const layouts = useMemo(() => {
    let x = START_X;
    return measures.map((measure, index) => {
      const width = measureWidths[index];
      const contentStart = x + (index === 0 ? FIRST_CONTENT_OFFSET : MEASURE_PADDING);
      let glyphX = contentStart;
      const items = measure.rhythms.map((symbol, rhythmIndex) => {
        const symbolWidth = rhythmComposeWidth(symbol);
        const item = { symbol, rhythmIndex, x: glyphX, width: symbolWidth };
        glyphX += symbolWidth + SYMBOL_GAP;
        return item;
      });
      const layout = { x, width, items };
      x += width;
      return layout;
    });
  }, [measures, measureWidths]);

  const endX = START_X + measureWidths.reduce((sum, width) => sum + width, 0);
  const totalWidth = endX + 10;
  const svgHeight = includeCounts ? 134 : 96;

  function commit(nextMeasures) {
    if (disabled || !onChange) return;
    onChange({
      ...value,
      rhythm: {
        ...(value.rhythm || {}),
        measures: nextMeasures
      }
    });
  }

  function cloneMeasures() {
    return measures.map((measure) => ({
      rhythms: [...measure.rhythms],
      counts: [...measure.counts],
      beamLinks: [...measure.beamLinks]
    }));
  }

  function addSymbol(symbol) {
    if (disabled) return;
    const next = cloneMeasures();
    const measure = next[currentMeasure];
    if (measure.rhythms.length > 0) measure.beamLinks.push(false);
    measure.rhythms.push(symbol);
    measure.counts.push("");
    commit(next);
  }

  function updateCount(measureIndex, rhythmIndex, nextValue) {
    if (disabled) return;
    const next = cloneMeasures();
    next[measureIndex].counts[rhythmIndex] = nextValue;
    commit(next);
  }

  function toggleBeam(measureIndex, gapIndex) {
    if (disabled) return;
    const next = cloneMeasures();
    const measure = next[measureIndex];
    const left = measure.rhythms[gapIndex];
    const right = measure.rhythms[gapIndex + 1];
    if (!isBeamableRhythm(left) || !isBeamableRhythm(right)) return;
    measure.beamLinks[gapIndex] = !measure.beamLinks[gapIndex];
    commit(next);
  }

  function deleteLast() {
    if (disabled) return;
    const next = cloneMeasures();
    const measure = next[currentMeasure];
    measure.rhythms.pop();
    measure.counts.pop();
    measure.beamLinks = measure.beamLinks.slice(0, Math.max(0, measure.rhythms.length - 1));
    commit(next);
  }

  function clearMeasure() {
    if (disabled) return;
    const next = cloneMeasures();
    next[currentMeasure] = { rhythms: [], counts: [], beamLinks: [] };
    commit(next);
  }

  useEffect(() => {
    if (disabled) return undefined;
    const node = containerRef.current;
    if (!node) return undefined;

    function onKeyDown(event) {
      const target = event.target;
      const isEditable = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;
      if (event.key === "Backspace" && !isEditable && phase === "compose") {
        event.preventDefault();
        deleteLast();
      }
    }

    node.addEventListener("keydown", onKeyDown);
    return () => node.removeEventListener("keydown", onKeyDown);
  }, [disabled, currentMeasure, measures, phase]);

  return (
    <QuestionLayout
      number={config.number}
      prompt={config.prompt}
      points={config.points}
      mode={mode}
      feedback={feedback}
      horizontalScrollRef={scrollRef}
    >
      <div
        ref={containerRef}
        tabIndex={0}
        onClick={() => containerRef.current?.focus()}
        style={{ outline: "none" }}
      >
        {!disabled && (
          <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => setPhase("compose")}
              style={{
                padding: "7px 11px",
                borderRadius: 6,
                border: phase === "compose" ? "2px solid #1976d2" : "1px solid #b8c0cc",
                background: phase === "compose" ? "#eef6ff" : "#fff",
                fontWeight: phase === "compose" ? 700 : 500
              }}
            >
              1. Compose rhythm
            </button>
            <button
              type="button"
              onClick={() => setPhase("beam")}
              style={{
                padding: "7px 11px",
                borderRadius: 6,
                border: phase === "beam" ? "2px solid #1976d2" : "1px solid #b8c0cc",
                background: phase === "beam" ? "#eef6ff" : "#fff",
                fontWeight: phase === "beam" ? 700 : 500
              }}
            >
              2. Beam notes
            </button>
          </div>
        )}

        {!disabled && phase === "compose" && (
          <>
            {/* {requiredSymbols.length > 0 && (
              <div style={{ marginBottom: 7, fontSize: 14, fontWeight: 600 }}>
                Use each rhythm below at least once.
              </div>
            )} */}
            <SymbolPalette
              allowedSymbols={allowedSymbols}
              selected={null}
              onSelect={addSymbol}
              disabled={disabled}
            />
          </>
        )}

        {!disabled && phase === "beam" && (
          <div style={{ marginBottom: 10, fontSize: 14, fontWeight: 600 }}>
            Click a gray dashed connection to add a beam. Click an existing beam to remove it. Secondary beams and hooks are drawn automatically from the note values.
          </div>
        )}

        <div ref={scrollRef} style={{ overflowX: "auto", maxWidth: "100%", paddingBottom: 4 }}>
          <svg
            width={totalWidth}
            height={svgHeight}
            role="img"
            aria-label={`Rhythm composition and beaming line in ${top}/${bottom}`}
            style={{ display: "block", overflow: "visible" }}
          >
            <line
              x1={START_X}
              x2={endX}
              y1={LINE_Y}
              y2={LINE_Y}
              stroke="#000"
              strokeWidth="1.4"
            />

            <line
              x1={START_X}
              x2={START_X}
              y1={BAR_TOP}
              y2={BAR_BOTTOM}
              stroke="#000"
              strokeWidth="1.4"
            />

            {layouts.map((layout, index) => {
              const measure = measures[index];
              const isLast = index === measureCount - 1;
              const part = feedback?.parts?.[index];
              const fill = mode === "review"
                ? part?.correct === true
                  ? "rgba(46,125,50,0.08)"
                  : part?.correct === false
                    ? "rgba(198,40,40,0.08)"
                    : "transparent"
                : index === currentMeasure
                  ? "rgba(25,118,210,0.06)"
                  : "transparent";

              return (
                <g key={index}>
                  <rect
                    x={layout.x + 1}
                    y={BAR_TOP - 8}
                    width={Math.max(1, layout.width - 2)}
                    height={BAR_BOTTOM - BAR_TOP + 16}
                    fill={fill}
                    onClick={() => !disabled && setCurrentMeasure(index)}
                    style={{ cursor: disabled ? "default" : "pointer" }}
                  />

                  {index === 0 && (
                    <g>
                      {renderTimeSignatureNumber(top, layout.x + 38, LINE_Y)}
                      {renderTimeSignatureNumber(bottom, layout.x + 38, LINE_Y + 20)}
                    </g>
                  )}

                  {layout.items.map((item) => {
                    const rhythmIndex = item.rhythmIndex;
                    const isBeamed = rhythmBeamDepth(item.symbol) > 0 && (
                      measure.beamLinks[rhythmIndex - 1] === true ||
                      measure.beamLinks[rhythmIndex] === true
                    );
                    const countBoxWidth = Math.max(46, Math.min(86, item.width + 8));
                    const countX = item.x + item.width / 2 - countBoxWidth / 2;
                    const countId = `m${index}-r${rhythmIndex}`;
                    const countPart = feedback?.countParts?.[countId];
                    const isCorrect = mode === "review" && countPart?.correct === true;
                    const isIncorrect = mode === "review" && countPart?.correct === false;
                    const border = isCorrect
                      ? "2px solid #2e7d32"
                      : isIncorrect
                        ? "2px solid #c62828"
                        : "1px solid #8b949e";
                    const background = isCorrect
                      ? "#e8f5e9"
                      : isIncorrect
                        ? "#fdecea"
                        : "#fff";

                    return (
                      <g key={`${item.symbol}-${rhythmIndex}`}>
                        <g transform={`translate(${item.x}, ${LINE_Y})`}>
                          <Glyph symbol={item.symbol} suppressFlag={isBeamed} />
                        </g>

                        {includeCounts && (
                          <foreignObject
                            x={countX}
                            y={COUNT_INPUT_Y}
                            width={countBoxWidth}
                            height={34}
                          >
                            <div
                              xmlns="http://www.w3.org/1999/xhtml"
                              style={{ width: "100%", height: "100%" }}
                              onClick={(event) => event.stopPropagation()}
                            >
                              <input
                                type="text"
                                inputMode="text"
                                autoComplete="off"
                                autoCapitalize="none"
                                spellCheck={false}
                                aria-label={`Measure ${index + 1}, rhythm ${rhythmIndex + 1} count`}
                                value={measure.counts[rhythmIndex] ?? ""}
                                disabled={disabled}
                                onFocus={() => !disabled && setCurrentMeasure(index)}
                                onChange={(event) => updateCount(index, rhythmIndex, event.target.value)}
                                style={{
                                  width: "100%",
                                  height: 30,
                                  boxSizing: "border-box",
                                  padding: "4px 5px",
                                  border,
                                  borderRadius: 5,
                                  background,
                                  color: "#111",
                                  textAlign: "center",
                                  fontSize: 15,
                                  lineHeight: 1.2
                                }}
                                title={isIncorrect && countPart?.expected
                                  ? `Correct count: ${countPart.expected}`
                                  : undefined}
                              />
                            </div>
                          </foreignObject>
                        )}
                      </g>
                    );
                  })}

                  <BeamOverlay
                    items={layout.items}
                    links={measure.beamLinks}
                    measureIndex={index}
                    mode={mode}
                    phase={phase}
                    feedback={feedback}
                    onToggle={(gapIndex) => toggleBeam(index, gapIndex)}
                  />

                  {!isLast && (
                    <line
                      x1={layout.x + layout.width}
                      x2={layout.x + layout.width}
                      y1={BAR_TOP}
                      y2={BAR_BOTTOM}
                      stroke="#000"
                      strokeWidth="1.4"
                    />
                  )}
                </g>
              );
            })}

            <line
              x1={endX - 5}
              x2={endX - 5}
              y1={BAR_TOP}
              y2={BAR_BOTTOM}
              stroke="#000"
              strokeWidth="1.4"
            />
            <line
              x1={endX}
              x2={endX}
              y1={BAR_TOP}
              y2={BAR_BOTTOM}
              stroke="#000"
              strokeWidth="3.2"
            />
          </svg>
        </div>

        {!disabled && (
          <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, color: "#555" }}>Editing measure {currentMeasure + 1}</span>
            <button type="button" onClick={deleteLast}>Delete last</button>
            <button type="button" onClick={clearMeasure}>Clear measure</button>
          </div>
        )}
      </div>
    </QuestionLayout>
  );
}
